/**
 * The `GeoPoint` type, how to decode one out of PostGIS, and how far apart two
 * of them are. One module because a coordinate that cannot be parsed and a
 * coordinate that cannot be measured are the same failure to every caller.
 *
 * Decoding: PostgREST hands the same column back in one of three shapes
 * depending on the PostGIS adapter in front of it — GeoJSON object, WKT text,
 * or EWKB hex — so the decode has to cover all three. It lived only in
 * `apps/admin` until the Scheduled availability service needed hub coordinates
 * server-side; one copy in two places is how the two drift, so it lives here
 * now.
 *
 * NEVER invents a fallback location: an unreadable value is `null`, which the
 * caller must surface as a missing-distance blocker rather than a guess. A hub
 * placed at 0,0 would silently pass the 15 km service-area test.
 */

export interface GeoPoint {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_KM = 6371;

/**
 * Great-circle distance in km (haversine).
 *
 * Straight-line, NOT road distance — it under-reads a real trip, so it may
 * price a **preview** and gate service-area eligibility, and it must never
 * settle money. Payable mileage is server-computed by PostGIS over
 * `trips.track` (`rules/money-math.md` §3; SECURITY §5).
 *
 * Moved here from `apps/app/src/mocks/hubs.ts` (MOCK-01) — it had no canonical
 * home, so the client's distance-surcharge preview was measuring with a copy
 * that lived in the mock engine.
 */
export function haversineKm(a: GeoPoint, b: GeoPoint): number {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/** GeoJSON/WKT/EWKB all store longitude first — the reverse of how we read it. */
function fromLngLat(lng: unknown, lat: unknown): GeoPoint | null {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function fromCoordinates(coordinates: unknown): GeoPoint | null {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  return fromLngLat(coordinates[0], coordinates[1]);
}

function fromEwkbHex(value: string): GeoPoint | null {
  // e.g. 0101000020E6100000{lng float64}{lat float64}. Byte order and the
  // optional SRID field are read from the payload rather than assuming one
  // PostgreSQL output format.
  const hex = value.replace(/^\\x/i, "");
  if (!/^[0-9a-f]+$/i.test(hex) || hex.length % 2 !== 0 || hex.length < 42) return null;

  const bytes = new Uint8Array(hex.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  const littleEndian = bytes[0] === 1;
  if (bytes[0] !== 0 && !littleEndian) return null;

  const view = new DataView(bytes.buffer);
  const type = view.getUint32(1, littleEndian);
  const hasSrid = (type & 0x20000000) !== 0;
  const coordinateOffset = 5 + (hasSrid ? 4 : 0);
  if ((type & 0xff) !== 1 || bytes.length < coordinateOffset + 16) return null;

  return fromLngLat(
    view.getFloat64(coordinateOffset, littleEndian),
    view.getFloat64(coordinateOffset + 8, littleEndian),
  );
}

/** Parse any known Point serialization, or `null` when there is no usable one. */
export function parseGeographyPoint(value: unknown): GeoPoint | null {
  if (value && typeof value === "object" && "coordinates" in value) {
    return fromCoordinates((value as { coordinates?: unknown }).coordinates);
  }
  if (typeof value !== "string") return null;

  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed && typeof parsed === "object" && "coordinates" in parsed) {
      return fromCoordinates((parsed as { coordinates?: unknown }).coordinates);
    }
  } catch {
    // Not JSON — WKT and EWKB are handled below.
  }

  const wkt = /^POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i.exec(value);
  if (wkt) return fromLngLat(Number(wkt[1]), Number(wkt[2]));

  return fromEwkbHex(value);
}
